import {
  CoverageNeed,
  NeedName,
  ProductCoverageName,
  ProductName,
} from "Store/akaAdvice/interfaces/CollectiveAdvice";

import { StyledRow } from "App/global-style";
import { Checkbox } from "Components/Form/Checkbox";
import { VALUE_TYPE } from "Components/NumberValue";
import { getPriceFormatFromValue } from "Formatting/index";
import { useIsAdvisorSelector } from "Store/advisor/advisorSelectors";
import { isNotDisplayed } from "Store/akaAdvice/interfaces/getters";
import { getCorrectAkaAdviceValues } from "Store/akaAdvice/mappers/mapToFormValues/utils";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { TextLanguage } from "Store/texts/interfaces";
import { FormikProps } from "formik";
import React from "react";
import { useAdviceIsLoading } from "../../../hooks/stateSelectors";
import { checkShouldPurchaseFromAdvisor } from "../../../utils";
import { InsurancePrice } from "../../AdvicePrice/InsurancePrice";
import { AkaDiscreteSlider } from "../../AkaDiscreteSlider/AkaDiscreteSlider";
import { CloseToMaxAgeWarning } from "../../CloseToMaxAgeWarning";
import { AkaAdvicePriceFormValues } from "../../interfaces";
import {
  DISABILITY_PENSION_END_AGE,
  DISABILITY_PENSION_MAX_AGE,
} from "../disabilityAdviceCardConstants";

const SLIDER_STEP = 10000;

interface Props {
  formProps: FormikProps<AkaAdvicePriceFormValues>;
  need: CoverageNeed;
  recalculatePrice: (
    formValues: AkaAdvicePriceFormValues,
    specificProduct: ProductName,
  ) => void;
  setPriceIsChanging: (value: boolean) => void;
  priceIsChanging: boolean;
  priceIsLoading: boolean;
  adviceCard: any;
  lang: TextLanguage;
}

export const DisabilityForm = ({
  formProps,
  need,
  recalculatePrice,
  setPriceIsChanging,
  priceIsChanging,
  priceIsLoading,
  adviceCard,
  lang,
}: Props): React.ReactElement | null => {
  const coverageName = ProductCoverageName.DISABILITY_FLEX_PENSION;
  const isAdvisor = useIsAdvisorSelector();
  const isLoading = useAdviceIsLoading();

  const { adviceValue } = getCorrectAkaAdviceValues(
    need,
    coverageName,
    isAdvisor,
  );
  const shouldPurchaseFromAdvisor = checkShouldPurchaseFromAdvisor(
    isAdvisor,
    need,
    coverageName,
  );

  if (isNotDisplayed(need, coverageName)) {
    return null;
  }

  return (
    <div>
      <StyledRow className="AkaAdviceInsuranceForm">
        <div>
          <Checkbox
            id="disabilityInsuredSumCheckbox"
            name="disabilityInsuredSumCheckbox"
            label="Storebrand uføreforsikring"
            hint={
              adviceValue > 0
                ? `Vi anbefaler en uførepensjon på ${getPriceFormatFromValue(
                    adviceValue,
                    VALUE_TYPE.NOK_YEAR,
                  )}`
                : undefined
            }
          />
          {formProps.values.disabilityInsuredSumCheckbox && (
            <AkaDiscreteSlider
              name="disabilityInsuredSumSlider"
              isAdvisor={isAdvisor}
              need={need}
              productCoverageName={coverageName}
              step={SLIDER_STEP}
              adviceValueMarkLabel={
                shouldPurchaseFromAdvisor
                  ? adviceCard.sliderValueMarker.maxAmount[lang]
                  : undefined
              }
              valueType={VALUE_TYPE.NOK}
              disabled={isLoading}
              label={adviceCard.sliderValueMarker.advicedAmount[lang]}
              onChange={() => setPriceIsChanging(true)}
              onChangeCommitted={(_, value) =>
                recalculatePrice(
                  {
                    ...formProps.values,
                    disabilityInsuredSumSlider: value,
                  },
                  INSURANCE_NAME_VALUE.DISABILITY_FLEX,
                )
              }
            />
          )}
        </div>
        <InsurancePrice
          needName={NeedName.DISABILITY}
          productCoverages={[coverageName]}
          priceIsChanging={priceIsChanging}
          priceIsLoading={priceIsLoading}
        />
      </StyledRow>
      <CloseToMaxAgeWarning
        need={need}
        coverageName={coverageName}
        warningTexts={adviceCard.warnings}
        lang={lang}
        warningVariables={{
          maxAge: DISABILITY_PENSION_MAX_AGE,
          endAge: DISABILITY_PENSION_END_AGE,
        }}
      />
    </div>
  );
};
